import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";

import { Menu, X, Sparkles, Lock, ShieldCheck } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";

const NAV = [
  { to: "/homes", label: "Homes", member: true },
  { to: "/vehicles", label: "Vehicles", member: true },
  { to: "/market", label: "Market Intelligence", member: true },
  { to: "/coverage", label: "Coverage Map", member: true },
  { to: "/pricing", label: "Pricing" },
];

export function TierBadge({ label }) {
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-mono-data uppercase tracking-wider text-accent border border-accent/40 rounded-full px-1.5 py-[3px] leading-none" data-testid={`tier-badge-${label.toLowerCase()}`}>
      <Lock className="w-2.5 h-2.5" />{label}
    </span>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { ent } = useBilling();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dealNew, setDealNew] = useState(false);

  const isLocked = (n) => n.member && user && ent && !ent.premium;

  useEffect(() => {
    if (!user) { setDealNew(false); return; }
    api.get("/deals/today").then((r) => {
      const d = r.data || {};
      const sig = `${d.date}|${d.property?.id || ""}|${d.vehicle?.id || ""}`;
      setDealNew(localStorage.getItem("luxora_seen_deal") !== sig);
    }).catch(() => {});
    const clear = () => setDealNew(false);
    window.addEventListener("luxora-deal-seen", clear);
    return () => window.removeEventListener("luxora-deal-seen", clear);
  }, [user, location.pathname]);

  const DealPulse = () => (
    <span data-testid="deal-new-pulse" className="ml-1.5 relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
    </span>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="app-header sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <span className="font-display font-extrabold text-xl tracking-tight">VAELITH</span>
            <span className="hidden sm:inline font-mono-data text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">Intelligence</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
                className={`text-sm transition-colors hover:text-foreground flex items-center ${location.pathname.startsWith(n.to) ? "text-foreground font-medium" : "text-muted-foreground"}`}
              >
                {n.label}
                {isLocked(n) && <Lock className="w-3 h-3 ml-1.5 text-muted-foreground/50 shrink-0" data-testid={`nav-lock-${n.label.split(" ")[0].toLowerCase()}`} aria-label="Members only" />}
              </Link>
            ))}
            {ent?.is_admin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`text-sm transition-colors hover:text-foreground flex items-center gap-1 ${location.pathname.startsWith("/admin") ? "text-accent font-medium" : "text-accent/70"}`}
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <NotificationBell />
            {user ? (
              <Link to="/dashboard" data-testid="nav-dashboard" className="relative">
                <Avatar className="w-9 h-9 border border-border">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback>{user.name?.[0] || "U"}</AvatarFallback>
                </Avatar>
                {(dealNew) && <span data-testid="deal-new-pulse" className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent border border-background" /></span>}
              </Link>
            ) : (
              <Link to="/login" className="hidden sm:flex">
                <Button data-testid="login-button" className="rounded-full">
                  Sign in
                </Button>
              </Link>
            )}
            <Button variant="ghost" size="icon" className="md:hidden rounded-full" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-border px-5 py-4 flex flex-col gap-4 bg-background" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm text-muted-foreground flex items-center">{n.label}{isLocked(n) && <Lock className="w-3 h-3 ml-1.5 text-muted-foreground/50 shrink-0" aria-label="Members only" />}</Link>
            ))}
            {ent?.is_admin && (
              <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin" className="text-sm text-accent flex items-center gap-1.5 font-medium"><ShieldCheck className="w-4 h-4" /> Admin</Link>
            )}
            {user && (
              <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-nav-dashboard" className="text-sm text-muted-foreground">My Account</Link>
            )}
            {user ? (
              <button onClick={logout} className="text-sm text-left text-muted-foreground" data-testid="mobile-logout">Sign out</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-left font-medium" data-testid="mobile-login">Sign in</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border mt-20">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-14 grid gap-x-8 gap-y-10 grid-cols-2 lg:grid-cols-4">
          <div className="col-span-2 lg:col-span-1">
            <div className="font-display font-extrabold text-lg tracking-tight">VAELITH</div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-xs">
              Verified luxury real estate &amp; automotive market intelligence. We display only verified data from licensed sources.
            </p>
          </div>
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Explore</div>
            <div className="flex flex-col gap-2.5 text-sm">
              <Link to="/homes" className="text-muted-foreground hover:text-foreground transition-colors">Luxury Homes</Link>
              <Link to="/vehicles" className="text-muted-foreground hover:text-foreground transition-colors">Luxury Vehicles</Link>
              <Link to="/market" className="text-muted-foreground hover:text-foreground transition-colors">Market Intelligence</Link>
              <Link to="/coverage" className="text-muted-foreground hover:text-foreground transition-colors">Coverage Map</Link>
            </div>
          </div>
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Data Integrity</div>
            <p className="text-sm text-muted-foreground leading-relaxed">No fabricated listings, prices, photos, or statistics. When verified data is unavailable, we say so.</p>
          </div>
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Verified AI</div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
              <p className="leading-relaxed">AI explains verified data only. It never invents facts.</p>
            </div>
          </div>
        </div>
        <div className="border-t border-border py-5 text-center font-mono-data text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} VAELITH — Verified Intelligence Platform
        </div>
      </footer>
    </div>
  );
}
